import { useEffect, useState, useRef } from "react"
import {
    Chip,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Box,
    Card,
    Avatar,
    Badge,
    Pagination,
    PaginationItem,
    Container,
    Button
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { axiosInstance } from "src/network/adapter"
import { ApiEndPoints } from "src/network/endpoints"
import { toastError } from "src/utils/utils"
import moment from "moment"
import { DefaultPaginationSettings } from "src/constants/general.const";

const BoldTypo = styled(Typography)({
    fontWeight: 'bold',
    fontSize: '18px',
    textTransform: 'capitalize',
});

const SimpleTypo = styled(Typography)({
    marginTop: "5px",
    fontSize: '14px',
});

const NotificationAvatar = styled(Avatar)({
    width: '2.5rem',
    height: '2.5rem',
    fontSize: '1.125rem',
    backgroundColor: '#25DB75',
    color: '#fff',
});

moment.locale('en', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'seconds',
        ss: '%ss',
        m: 'a minute',
        mm: '%d minutes',
        h: 'an hour',
        hh: '%d hours',
        d: 'a day',
        dd: '%d days',
        M: 'a month',
        MM: '%d months',
        y: 'a year',
        yy: '%d years'
    }
});

const NotificationPage = () => {
    const navigate = useNavigate();
    const searchTimeoutRef = useRef();

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('')
    const [notificationData, setNotificationData] = useState([]);
    const [search, setSearch] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DefaultPaginationSettings.ROWS_PER_PAGE);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchData = ({ currentPage, pageSize = 10, search, status }) => {
        setLoading(true);
        let params = {
            page: currentPage,
            limit: pageSize,
        };

        axiosInstance
            .get(ApiEndPoints.NOTIFICATION.list, { params })
            .then((response) => {
                console.log("Notification response", response?.data?.data)
                if (response?.data?.data?.notifications) {
                    setNotificationData(response?.data?.data?.notifications)
                    setTotalCount(response?.data?.data?.totalCount || 0)
                    setUnreadCount(response?.data?.data?.unreadCount || 0)
                } else {
                    setNotificationData([])
                    setTotalCount(0)
                    setUnreadCount(0)
                }
            })
            .catch((error) => {
                console.error('Error fetching notifications:', error);
                toastError(error);
                setNotificationData([])
                setTotalCount(0)
                setUnreadCount(0)
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const markAllAsRead = () => {
        setLoading(true);
        axiosInstance
            .patch(ApiEndPoints.NOTIFICATION.markAllAsRead)
            .then((response) => {
                console.log('All notifications marked as read:', response.data);
                fetchData({ currentPage: currentPage });
                // Dispatch custom event to update notification dropdown
                window.dispatchEvent(new CustomEvent('notifications-updated'));
            })
            .catch((error) => {
                console.error('Error marking all notifications as read:', error);
                toastError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const markAsRead = (id) => {
        setLoading(true);
        axiosInstance
            .patch(ApiEndPoints.NOTIFICATION.markAsRead(id))
            .then((response) => {
                console.log('Notification marked as read:', response.data);
                fetchData({ currentPage: currentPage });
                // Dispatch custom event to update notification dropdown
                window.dispatchEvent(new CustomEvent('notifications-updated'));
            })
            .catch((error) => {
                console.error('Error marking notification as read:', error);
                toastError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // useEffect(() => {
    //     fetchData({
    //         currentPage: currentPage,
    //     });
    // }, [currentPage]);

    const handleSearchChange = (e) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearch(e.target.value);
            fetchData({
                currentPage: 1,
                search: e.target.value,
                status: status
            });
        }, 500)
    }

    const handleNotificationClick = (notification) => {
        // Mark notification as read first
        if (!notification.is_dealer_read) {
            markAsRead(notification.id);
        }

        // Navigate based on notification type
        const { type, meta_data } = notification;
        
        switch (type) {
            case 'ad_hoc_quote_accepted':
            case 'ad_hoc_quote_rejected':
                if (meta_data?.bookingId) {
                    navigate(`/task-execution/task-adhoc-detail/${meta_data.bookingId}`);
                } else {
                    navigate('/task-execution');
                }
                break;
                
            case 'new_booking_received':
            case 'booking_canceled':
            case 'booking_completed':
            case 'booking_reassigned':
                if (meta_data?.bookingId) {
                    navigate(`/booking-requests/booking-detail/${meta_data.bookingId}`);
                } else {
                    navigate('/booking-requests');
                }
                break;
                
            case 'staff_added':
                navigate('/staff-management');
                break;

            case 'mechanic_task_completed':
                if (meta_data?.bookingId) {
                    navigate(`/job-archive/${meta_data.bookingId}`);
                } else {
                    navigate('/job-archive');
                }
                break;

            case 'new_job_assigned':
            case 'mechanic_issue_accepted':
            case 'mechanic_issue_rejected':
                if (meta_data?.bookingId) {
                    navigate(`/task-execution/task-basic-detail/${meta_data.bookingId}`);
                } else {
                    navigate('/task-execution');
                }
                break;

                case 'chat' : navigate('/chat'); break;
            default:
                // For unknown types, navigate to general notification page
                navigate(`/notification-detail/${notification.id}`);
                break;
        }
    }

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'new_booking_received':
                return '📋';
            case 'booking_canceled':
                return '❌';
            case 'booking_completed':
                return '✅';
            case 'ad_hoc_quote_accepted':
                return '✅';
            case 'ad_hoc_request_received':
                return '📝';
            case 'mechanic_task_completed':
                return '🔧';
            case 'new_job_assigned':
                return '📋';
            case 'job_canceled':
                return '❌';
            case 'mechanic_issue_accepted':
                return '✅';
            case 'mechanic_issue_rejected':
                return '❌';
            case 'job_marked_complete':
                return '✅';
            default:
                return '🔔';
        }
    }

    const getNotificationTypeLabel = (type) => {
        switch (type) {
            case 'new_booking_received':
                return 'New Booking';
            case 'booking_canceled':
                return 'Booking Cancelled';
            case 'booking_completed':
                return 'Booking Completed';
            case 'ad_hoc_quote_accepted':
                return 'Quote Accepted';
            case 'ad_hoc_request_received':
                return 'Ad-Hoc Request';
            case 'mechanic_task_completed':
                return 'Mechanic Task Completed';
            case 'new_job_assigned':
                return 'New Job Assigned';
            case 'job_canceled':
                return 'Job Cancelled';
            case 'mechanic_issue_accepted':
                return 'Issue Accepted';
            case 'mechanic_issue_rejected':
                return 'Issue Rejected';
            case 'job_marked_complete':
                return 'Job Completed';
            default:
                return 'Notification';
        }
    }

    const getNotificationColor = (type) => {
        switch (type) {
            case 'new_booking_received':
                return 'success';
            case 'booking_canceled':
                return 'error';
            case 'booking_completed':
                return 'success';
            case 'ad_hoc_quote_accepted':
                return 'primary';
            case 'ad_hoc_request_received':
                return 'warning';
            case 'mechanic_task_completed':
                return 'success';
            case 'new_job_assigned':
                return 'info';
            case 'job_canceled':
                return 'error';
            case 'mechanic_issue_accepted':
                return 'success';
            case 'mechanic_issue_rejected':
                return 'error';
            case 'job_marked_complete':
                return 'success';
            default:
                return 'default';
        }
    }

    return (
        <>
            <Container maxWidth="xl">
                <Grid container>
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                Notifications ({unreadCount} unread)
                            </Typography>
                            <Button variant="contained" onClick={markAllAsRead} disabled={loading}>
                                {loading ? 'Marking...' : 'Mark All as Read'}
                            </Button>
                        </Box>

                        <Card sx={{
                            width: "100%",
                            bgcolor: "#FFFFFF",
                            boxShadow: '0px 0px 25px 7px rgba(0, 0, 0, 0.03)'
                        }}>
                            <TableContainer>
                                <Table>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography>Loading notifications...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : notificationData && notificationData.length > 0 ? (
                                            notificationData.map((item, index) => (
                                                <TableRow
                                                    hover
                                                    key={index}
                                                    onClick={() => handleNotificationClick(item)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        backgroundColor: !item.is_dealer_read ? '#f8f9fa' : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: !item.is_dealer_read ? '#e9ecef' : '#f5f5f5',
                                                        }
                                                    }}
                                                >
                                                    <TableCell sx={{ p: 3 }}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                            <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                                                                <NotificationAvatar sx={{ mr: 2, mt: 0.5 }}>
                                                                    {item.title ? item.title.charAt(0).toUpperCase() : 'N'}
                                                                </NotificationAvatar>

                                                                <Box sx={{ flex: 1 }}>
                                                                    <BoldTypo sx={{
                                                                        fontWeight: !item.is_dealer_read ? 700 : 600,
                                                                        color: !item.is_dealer_read ? '#000' : 'inherit'
                                                                    }}>
                                                                        {item.title}
                                                                    </BoldTypo>

                                                                    <SimpleTypo sx={{
                                                                        fontWeight: !item.is_dealer_read ? 500 : 400,
                                                                        color: !item.is_dealer_read ? '#333' : '#666',
                                                                        mt: 1
                                                                    }}>
                                                                        {item.body}
                                                                    </SimpleTypo>

                                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                                                                        <Chip
                                                                            label={getNotificationTypeLabel(item.type)}
                                                                            color={getNotificationColor(item.type)}
                                                                            size="small"
                                                                            sx={{
                                                                                height: "24px",
                                                                                fontSize: "11px",
                                                                                fontWeight: 600,
                                                                                textTransform: 'capitalize'
                                                                            }}
                                                                        />

                                                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                                                            {moment(item.created_at).fromNow()}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {!item.is_dealer_read && (
                                                                <Box
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#25DB75',
                                                                        ml: 1,
                                                                        mt: 0.5,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No notifications found.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
            {totalCount > pageSize && (
                <Grid container justifyContent="center" sx={{ mt: 3 }}>
                    <Pagination
                        count={Math.ceil(totalCount / pageSize)}
                        page={currentPage}
                        onChange={(event, newPage) => setCurrentPage(newPage)}
                        color="primary"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: item.selected ? "primary.main" : "transparent",
                                    color: item.selected ? "white" : "primary.main",
                                }}
                            />
                        )}
                    />
                </Grid>
            )}
        </>
    )
}

export default NotificationPage
